import { useEffect, useState } from 'react';
import {
  MOVING_DESIGNS,
  PROMPT_REPEAT_MIN_SECONDS,
  ROUND_TRANSITION_MAX_MS,
  ROUND_TRANSITION_MIN_MS,
  useSettings,
  type RoundTransitionType,
  type UserSettings,
} from '../settings/SettingsContext';
import LearnersSection from '../settings/LearnersSection';
import FeaturesSection from '../settings/FeaturesSection';
import RewardSection from '../settings/RewardSection';
import RewardItemsSection from '../settings/RewardItemsSection';
import SupervisorPinSection from '../settings/SupervisorPinSection';
import PeopleSection from '../settings/PeopleSection';
import { whoAmI } from '../account/sharing';
import TermsSection from '../settings/TermsSection';
import Privacy from '../privacy/privacy';
import DownloadDataSection from '../settings/DownloadDataSection';
import TwoFactorSection from '../settings/TwoFactorSection';
import VoicePreview from '../settings/VoicePreview';
import LanguagePicker from '../i18n/LanguagePicker';
import { ALL_KINDS, KINDS, type ConceptKind, type ParamDef } from '../concepts/kinds';
import { translate, useT } from '../i18n/i18n';
import { useEntitlements } from '../subscription/offers';

type ParamValue = string | number | boolean;

const TABS = [
  { id: 'learners', label: 'Learners' },
  { id: 'features', label: 'Features' },
  { id: 'games', label: 'Games' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'rewards', label: 'Rewards' },
  { id: 'account', label: 'Account' },
  { id: 'people', label: 'People' },
  { id: 'terms', label: 'Terms' },
  { id: 'privacy', label: 'Privacy' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/** The tabs whose controls are part of the one saved draft. */
const SAVES_DRAFT: TabId[] = ['games', 'appearance'];

const TAB_KEY = 'el.settings.tab';

function rememberedTab(): TabId {
  try {
    const v = localStorage.getItem(TAB_KEY);
    if (v && TABS.some((t) => t.id === v)) return v as TabId;
  } catch {
    /* a blocked or cleared store simply means no preference */
  }
  return 'learners';
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size) as T[]);
  return out;
}

/** What this kind is set to, or the kind's own fallback if nothing is set. */
function kindValue(settings: UserSettings, kind: ConceptKind, param: ParamDef): ParamValue {
  const forKind = settings.kindDefaults?.[kind];
  const stored = forKind ? forKind[param.id] : undefined;
  if (stored === undefined || stored === null) return param.fallback;
  return stored as ParamValue;
}

/**
 * One field, drawn from the declaration rather than written out per parameter.
 *
 * A NEW PARAMETER SHOULD NOT TOUCH THIS PAGE. That is the whole point of the
 * declarations: a kind states what it has and the form follows. Writing the
 * fields by hand is what left a tile count on a page shared with games that
 * have no tiles.
 */
function KindParamField({
  kind, param, value, onChange,
}: {
  kind: ConceptKind;
  param: ParamDef;
  value: ParamValue;
  onChange: (v: ParamValue) => void;
}) {
  const t = useT();
  const id = `${kind}-${param.id}`;
  return (
    <div className="field">
      {param.type === 'toggle' ? (
        <>
          <span className="field-label">{t(param.label)}</span>
          <label className="check">
            <input
              id={id}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
            {t(param.label)}
          </label>
        </>
      ) : (
        <>
          <label htmlFor={id}>{t(param.label)}</label>
          {param.type === 'preset' ? (
            <select id={id} value={String(value)} onChange={(e) => onChange(e.target.value)}>
              {param.options.map((o) => (
                <option key={o.value} value={o.value}>{t(o.label)}</option>
              ))}
            </select>
          ) : (
            <input
              id={id}
              type="number"
              min={param.min}
              max={param.max}
              value={Number(value)}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isNaN(n)) return;
                onChange(Math.min(param.max, Math.max(param.min, n)));
              }}
            />
          )}
        </>
      )}
      {param.hint && <span className="field-hint">{t(param.hint)}</span>}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  design: 'Theme on teacher screens',
  gameDesign: 'Theme on game screens',
  palette: 'Colours',
  textScale: 'Text size',
  lineSpacing: 'Line spacing',
  borderWeight: 'Outlines',
  feedbackStrength: 'Correct-answer signal',
  tileSize: 'Tile size',
  defaultErrorless: 'Errorless teaching',
  ttsVoice: 'Voice',
  ttsRate: 'Voice rate',
  promptRepeatSeconds: 'Repeat audio prompt every',
  roundTransitionType: 'Between-rounds animation',
  roundTransitionMs: 'Animation duration',
};

function saveErrorMessage(e: unknown): string {
  const fields = (e as { body?: { fields?: Record<string, string> } } | null)?.body?.fields;
  const names = fields ? Object.keys(fields) : [];
  if (names.length > 0) {
    const labels = names.map((f) => (FIELD_LABELS[f] ? translate(FIELD_LABELS[f]) : f));
    return translate('Nothing was saved, because this choice was not accepted: {labels}. Pick a different option for it and save again.', { labels: labels.join(', ') });
  }
  return e instanceof Error ? e.message : translate('Could not save settings.');
}

export default function SettingsPage() {
  const t = useT();
  const { settings, update } = useSettings();
  const [tab, setTab] = useState<TabId>(rememberedTab);
  const [draft, setDraft] = useState<UserSettings>(settings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [motionTurnedOff, setMotionTurnedOff] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [helping, setHelping] = useState(false);
  const entitlements = useEntitlements();
  const unpaid = entitlements !== null && !entitlements.main && !entitlements.talkBoard;

  /* Somebody working in another person's account keeps the People tab whatever the owner has
     switched on — it holds the way out, and an owner turning sharing off would otherwise shut
     the door behind them. */
  useEffect(() => {
    let cancelled = false;
    void whoAmI()
      .then((w) => { if (!cancelled) setHelping(!w.ownAccount); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => setDraft(settings), [settings]);

  useEffect(() => {
    try {
      localStorage.setItem(TAB_KEY, tab);
    } catch {
      /* not being able to remember the tab is not worth telling anyone about */
    }
  }, [tab]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    const refresh = () => setVoices(window.speechSynthesis.getVoices());
    refresh();
    window.speechSynthesis.addEventListener('voiceschanged', refresh);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', refresh);
  }, []);

  function patchKind(kind: ConceptKind, paramId: string, value: ParamValue) {
    setDraft((d) => ({
      ...d,
      kindDefaults: {
        ...(d.kindDefaults ?? {}),
        [kind]: { ...((d.kindDefaults ?? {})[kind] ?? {}), [paramId]: value },
      },
    }));
    if (success) setSuccess(null);
    if (error) setError(null);
  }

  function chooseDesign(key: 'design' | 'gameDesign', val: UserSettings['design']) {
    patch(key, val);
    if (MOVING_DESIGNS.includes(val) && draft.reduceMotion) {
      patch('reduceMotion', false);
      setMotionTurnedOff(true);
    }
  }

  function patch<K extends keyof UserSettings>(key: K, val: UserSettings[K]) {
    setDraft((d) => ({ ...d, [key]: val }));
    // Clear stale feedback as soon as the supervisor starts editing again — prevents
    // "Settings saved." from lingering once the form has new unsaved changes.
    if (success) setSuccess(null);
    if (error) setError(null);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const clean: UserSettings = {
        ...draft,
        promptRepeatSeconds: Math.max(PROMPT_REPEAT_MIN_SECONDS, draft.promptRepeatSeconds),
        roundTransitionMs: Math.min(
          ROUND_TRANSITION_MAX_MS,
          Math.max(ROUND_TRANSITION_MIN_MS, draft.roundTransitionMs),
        ),
      };
      await update(clean);
      setSuccess(t('Settings saved.'));
    } catch (e) {
      setError(saveErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const kindsWithSettings = ALL_KINDS.filter((k) => KINDS[k].params.some((p) => !p.programOnly));

  /* A TAB FOR SOMETHING SWITCHED OFF IS A ROOM WITH NOTHING IN IT. Features decides what this
     account does; these follow it.

     Rewards also appears when they are actually being given, even with the feature off. The
     switch that stops them lives inside that tab, so hiding it on the flag alone would leave
     somebody collecting stars with no way to reach the setting that ends it. */
  const showRewards = settings.features.rewards || settings.reward.enabled;
  const showPeople = settings.features.secondAdult || helping;
  const UNPAID_TABS: TabId[] = ['account', 'people', 'terms', 'privacy'];
  const shownTabs = TABS.filter((tb) =>
    (tb.id !== 'rewards' || showRewards) && (tb.id !== 'people' || (unpaid ? helping : showPeople))
    && (!unpaid || UNPAID_TABS.includes(tb.id)));

  useEffect(() => {
    if (!shownTabs.some((tb) => tb.id === tab)) {
      setTab(shownTabs[0].id);
    }
  }, [tab, shownTabs]);

  return (
    <section aria-labelledby="settings-h">
      <h2 id="settings-h" className="visually-hidden">{t('Settings')}</h2>

      <div className="tabs" role="tablist" aria-label={t('Settings')}>
        {shownTabs.map((tb) => (
          <button
            key={tb.id}
            id={`tab-${tb.id}`}
            className={`tab${tab === tb.id ? ' is-active' : ''}`}
            role="tab"
            type="button"
            aria-selected={tab === tb.id}
            aria-controls={`panel-${tb.id}`}
            onClick={(e) => { setTab(tb.id); e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }}
          >
            {t(tb.label)}
          </button>
        ))}
      </div>

      <div id="panel-learners" role="tabpanel" aria-labelledby="tab-learners" hidden={tab !== 'learners'}>
        <LearnersSection />
      </div>

      <div id="panel-features" role="tabpanel" aria-labelledby="tab-features" hidden={tab !== 'features'}>
        <FeaturesSection />
      </div>

      {showRewards && (
        <div id="panel-rewards" role="tabpanel" aria-labelledby="tab-rewards" hidden={tab !== 'rewards'}>
          <RewardSection />
          {settings.features.rewardSpending && settings.reward.enabled && <RewardItemsSection />}
        </div>
      )}

      <div id="panel-account" role="tabpanel" aria-labelledby="tab-account" hidden={tab !== 'account'}>
        {!unpaid && <SupervisorPinSection />}
        <TwoFactorSection />
        <DownloadDataSection />
      </div>

      {showPeople && (
        <div id="panel-people" role="tabpanel" aria-labelledby="tab-people" hidden={tab !== 'people'}>
          <PeopleSection />
        </div>
      )}

      <div id="panel-terms" role="tabpanel" aria-labelledby="tab-terms" hidden={tab !== 'terms'}>
        <TermsSection />
      </div>

      <div id="panel-privacy" role="tabpanel" aria-labelledby="tab-privacy" hidden={tab !== 'privacy'}>
        <div className="card">
          <h2>{t('Privacy')}</h2>
          <Privacy />
        </div>
      </div>

      <div id="panel-games" role="tabpanel" aria-labelledby="tab-games" hidden={tab !== 'games'}>
        <div className="card">
          <h2>{t("The learner's board")}</h2>
          <div className="row">
            <div className="field">
              <label htmlFor="tileSize">{t('Tile size')}</label>
              <select
                id="tileSize"
                value={draft.tileSize}
                onChange={(e) => patch('tileSize', e.target.value as UserSettings['tileSize'])}
              >
                <option value="regular">{t('Regular')}</option>
                <option value="large">{t('Large')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="feedbackStrength">{t('Correct-answer signal')}</label>
              <select
                id="feedbackStrength"
                value={draft.feedbackStrength}
                onChange={(e) => patch('feedbackStrength', e.target.value as UserSettings['feedbackStrength'])}
              >
                <option value="subtle">{t('Subtle — outline only')}</option>
                <option value="regular">{t('Regular')}</option>
                <option value="strong">{t('Strong — heavier outline')}</option>
              </select>
              <span className="field-hint">
                {t('How clearly a right answer is marked. A wrong tap never gets any signal at any setting — it simply repeats the prompt.')}
              </span>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="defaultErrorless">{t('Errorless teaching')}</label>
              <select
                id="defaultErrorless"
                value={draft.defaultErrorless}
                onChange={(e) =>
                  patch('defaultErrorless', e.target.value as UserSettings['defaultErrorless'])
                }
              >
                <option value="off">{t('Off — wrong taps repeat the prompt')}</option>
                <option value="on">{t('On — only the right tile responds')}</option>
              </select>
              <span className="field-hint">
                {t('The starting point for new programs. Any program can be set differently in its own settings.')}
              </span>
            </div>
          </div>
        </div>

        {/* ONE CARD PER KIND OF GAME, AND ONLY FOR KINDS THAT HAVE SETTINGS.
            This was a single card called "Game defaults" holding a tile count and
            a unique-word count, as though every game were a grid of tiles. Of the
            hundred concepts, 43 are not: a supervisor-scored trial has no tiles
            and a sequence's length is its content. Each kind now declares what it
            has, and a kind that declares nothing shows nothing rather than
            offering a number that cannot be applied to it. */}
        {kindsWithSettings.map((kind) => (
          <div className="card" key={kind}>
            <h2>{t(KINDS[kind].label)}</h2>
            <p className="field-hint" style={{ marginTop: 0 }}>
              {t(KINDS[kind].describes)} {t('These apply to new programs of this kind; any program can be set differently in its own settings.')}
            </p>
            {chunk(KINDS[kind].params.filter((p) => !p.programOnly), 2).map((pair, row) => (
              <div className="row" key={row}>
                {pair.map((param) => (
                  <KindParamField
                    key={param.id}
                    kind={kind}
                    param={param}
                    value={kindValue(draft, kind, param)}
                    onChange={(v) => patchKind(kind, param.id, v)}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div id="panel-appearance" role="tabpanel" aria-labelledby="tab-appearance" hidden={tab !== 'appearance'}>
        <div className="card">
          <LanguagePicker id="settings-language" />
        </div>
        <div className="card">
          <h2>{t('Appearance')}</h2>
          <p className="field-hint" style={{ marginTop: 0 }}>
            {t('Sensory preferences differ a great deal from person to person. Set these to suit the learner rather than to any standard.')}
          </p>
          <div className="row">
            <div className="field">
              <label htmlFor="design">{t('Theme on teacher screens')}</label>
              <select
                id="design"
                value={draft.design}
                onChange={(e) => chooseDesign('design', e.target.value as UserSettings['design'])}
              >
                <option value="classic">{t('Classic — the original look')}</option>
                <option value="scholar">{t('Scholar — serif headings, soft cards')}</option>
                <option value="fresh">{t('Fresh — bold sans, green and orange')}</option>
                <option value="pastel">{t('Pastel — flat cards on warm pastels')}</option>
                <option value="symbol">{t('Symbol — extra-legible, clear outlines')}</option>
                <option value="classroom">{t('Classroom — big type, rounded pills')}</option>
                <option value="tactile">{t('Tactile — chunky buttons you can press')}</option>
                <option value="clinical">{t('Clinical — compact and businesslike')}</option>
                <option value="editorial">{t('Editorial — cream paper and serif')}</option>
                <option value="bold">{t('Bold — thick outlines, flat colour')}</option>
                <option value="night">{t('Night — modern dark')}</option>
                <option value="flow">{t('Flow — smooth gliding entrances (moves)')}</option>
                <option value="kinetic">{t('Kinetic — lines and fills that draw in (moves)')}</option>
                <option value="spring">{t('Spring — gentle bounce (moves)')}</option>
                <option value="playroom">{t('Playroom — 3D home: programs as books on a shelf')}</option>
                <option value="expedition">{t('Expedition — 3D home: programs as places on an island')}</option>
                <option value="garden">{t('Garden — 3D home: programs grow as plants')}</option>
                <option value="popup">{t('Pop-up book — 3D home: a pop-up page for each category')}</option>
                <option value="blocks">{t('Block city — 3D home: a tower of blocks for each category')}</option>
                <option value="glass">{t('Liquid glass — frosted glass over a slow colour wash (moves when Reduce motion is off)')}</option>
                <option value="silk">{t('Silk — satin cards and serif headings over a soft rippling wash (moves when Reduce motion is off)')}</option>
                <option value="chrome">{t('Liquid chrome — dark brushed metal and a liquid-metal logo (moves when Reduce motion is off)')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="gameDesign">{t('Theme on game screens')}</label>
              <select
                id="gameDesign"
                value={draft.gameDesign}
                onChange={(e) => chooseDesign('gameDesign', e.target.value as UserSettings['gameDesign'])}
              >
                <option value="classic">{t('Classic — the original look')}</option>
                <option value="scholar">{t('Scholar — serif headings, soft cards')}</option>
                <option value="fresh">{t('Fresh — bold sans, green and orange')}</option>
                <option value="pastel">{t('Pastel — flat cards on warm pastels')}</option>
                <option value="symbol">{t('Symbol — extra-legible, clear outlines')}</option>
                <option value="classroom">{t('Classroom — big type, rounded pills')}</option>
                <option value="tactile">{t('Tactile — chunky buttons you can press')}</option>
                <option value="clinical">{t('Clinical — compact and businesslike')}</option>
                <option value="editorial">{t('Editorial — cream paper and serif')}</option>
                <option value="bold">{t('Bold — thick outlines, flat colour')}</option>
                <option value="night">{t('Night — modern dark')}</option>
                <option value="flow">{t('Flow — smooth gliding entrances (moves)')}</option>
                <option value="kinetic">{t('Kinetic — lines and fills that draw in (moves)')}</option>
                <option value="spring">{t('Spring — gentle bounce (moves)')}</option>
                <option value="playroom">{t('Playroom — rounded wooden-toy buttons')}</option>
                <option value="expedition">{t('Expedition — field-journal serif and rules')}</option>
                <option value="garden">{t('Garden — soft, round and leafy')}</option>
                <option value="popup">{t('Pop-up book — paper cards and page tabs')}</option>
                <option value="blocks">{t('Block city — chunky outlined blocks')}</option>
                <option value="glass">{t('Liquid glass — rounded, light and clear')}</option>
                <option value="silk">{t('Silk — soft satin tiles')}</option>
                <option value="chrome">{t('Liquid chrome — dark metal tiles')}</option>
              </select>
              {draft.reduceMotion && (MOVING_DESIGNS.includes(draft.design) || MOVING_DESIGNS.includes(draft.gameDesign)) && (
                <span className="field-hint">
                  {t('The movement in this theme only shows when Reduce motion, further down, is turned off.')}
                </span>
              )}
              {motionTurnedOff && !draft.reduceMotion && (
                <span className="field-hint" role="status">
                  {t('Reduce motion has been turned off so this theme can move. Tick Reduce motion further down to stop all movement again.')}
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label htmlFor="palette">{t('Colours')}</label>
              <select
                id="palette"
                value={draft.palette}
                onChange={(e) => patch('palette', e.target.value as UserSettings['palette'])}
              >
                <option value="theme">{t("The theme's own colours")}</option>
                <option value="warm">{t('Warm — sand and sage')}</option>
                <option value="cool">{t('Cool — slate and teal')}</option>
                <option value="stone">{t('Stone — grey and indigo')}</option>
                <option value="lavender">{t('Lavender — soft lilac')}</option>
                <option value="dark">{t('Dark — deep sage')}</option>
                <option value="midnight">{t('Midnight — dark navy')}</option>
                <option value="graphite">{t('Graphite — charcoal and teal')}</option>
                <option value="indigo">{t('Indigo — white and indigo')}</option>
                <option value="meadow">{t('Meadow — fresh green')}</option>
                <option value="peach">{t('Peach — warm pastel and lilac')}</option>
                <option value="sunrise">{t('Sunrise — white and deep orange')}</option>
                <option value="sky">{t('Sky — clear blue')}</option>
                <option value="ocean">{t('Ocean — grey and navy blue')}</option>
                <option value="paper">{t('Paper — cream and black ink')}</option>
                <option value="chalk">{t('Chalk — white, black and yellow')}</option>
                <option value="wood">{t('Wood — honey wood and cream')}</option>
                <option value="parchment">{t('Parchment — old paper and rust')}</option>
                <option value="garden">{t('Garden — leaf green and sunshine')}</option>
                <option value="storybook">{t('Storybook — ink blue on paper')}</option>
                <option value="toybox">{t('Toybox — bright blue and yellow')}</option>
                <option value="glacier">{t('Glacier — ice blue and indigo')}</option>
                <option value="blush">{t('Blush — peach, rose and wine')}</option>
                <option value="steel">{t('Steel — dark graphite and silver')}</option>
                <option value="high-contrast">{t('High contrast')}</option>
              </select>
              <span className="field-hint">
                {t('All of them meet the same contrast standard. Pick whichever is easiest to look at.')}
              </span>
            </div>
            <div className="field">
              <label htmlFor="textScale">{t('Text size')}</label>
              <select
                id="textScale"
                value={draft.textScale}
                onChange={(e) => patch('textScale', e.target.value as UserSettings['textScale'])}
              >
                <option value="regular">{t('Regular')}</option>
                <option value="large">{t('Large')}</option>
                <option value="larger">{t('Larger')}</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="lineSpacing">{t('Line spacing')}</label>
              <select
                id="lineSpacing"
                value={draft.lineSpacing}
                onChange={(e) => patch('lineSpacing', e.target.value as UserSettings['lineSpacing'])}
              >
                <option value="regular">{t('Regular')}</option>
                <option value="relaxed">{t('Relaxed')}</option>
                <option value="loose">{t('Loose')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="borderWeight">{t('Outlines')}</label>
              <select
                id="borderWeight"
                value={draft.borderWeight}
                onChange={(e) => patch('borderWeight', e.target.value as UserSettings['borderWeight'])}
              >
                <option value="regular">{t('Regular')}</option>
                <option value="strong">{t('Stronger')}</option>
              </select>
              <span className="field-hint">
                {t('Makes the edges of buttons and boxes heavier.')}
              </span>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <span className="field-label">{t('Movement')}</span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={draft.reduceMotion}
                  onChange={(e) => patch('reduceMotion', e.target.checked)}
                />
                {t('Reduce motion')}
              </label>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="voice">{t('Voice (audio prompts)')}</label>
              <select
                id="voice"
                value={draft.ttsVoice ?? 'el:gb-c'}
                onChange={(e) => patch('ttsVoice', e.target.value)}
              >
                <optgroup label={t('EasyLearning voices')}>
                  <option value="el:gb-c">{t('Clara, British woman (default)')}</option>
                  <option value="el:gb-a">{t('Grace, British woman')}</option>
                  <option value="el:gb-b">{t('Oliver, British man')}</option>
                  <option value="el:studio-c">{t('Harriet, British woman')}</option>
                  <option value="el:aoede">{t('Aoede, British woman')}</option>
                  <option value="el:achernar">{t('Achernar, British woman')}</option>
                </optgroup>
                <optgroup label={t('Earlier EasyLearning voices')}>
                  <option value="el:emma">{t('Emma, British woman')}</option>
                  <option value="el:alice">{t('Alice, British woman')}</option>
                  <option value="el:george">{t('George, British man')}</option>
                  <option value="el:heart">{t('Heart, American woman')}</option>
                  <option value="el:cori">{t('Cori, British woman')}</option>
                  <option value="el:alba">{t('Alba, Scottish woman')}</option>
                  <option value="el:northern">{t('Northern English man')}</option>
                </optgroup>
                <optgroup label={t('Voices on this device')}>
                  <option value="device">{t("This device's default voice")}</option>
                  {draft.ttsVoice && draft.ttsVoice !== 'device' && !draft.ttsVoice.startsWith('el:')
                    && !voices.some((v) => v.name === draft.ttsVoice) && (
                    <option value={draft.ttsVoice}>{t('{name} (not on this device)', { name: draft.ttsVoice })}</option>
                  )}
                  {voices.map((v) => (
                    <option key={`${v.name}-${v.lang}`} value={v.name}>
                      {v.name} ({v.lang})
                    </option>
                  ))}
                </optgroup>
              </select>
              <VoicePreview voiceName={draft.ttsVoice} rate={draft.ttsRate} />
            </div>
            <div className="field">
              <label htmlFor="rate">{t('Voice rate ({rate})', { rate: draft.ttsRate.toFixed(2) })}</label>
              <input
                id="rate"
                type="range"
                min={0.5}
                max={1.5}
                step={0.05}
                value={draft.ttsRate}
                onChange={(e) => patch('ttsRate', Number(e.target.value))}
              />
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="promptRepeat">
                {t('Repeat audio prompt every (seconds, minimum {min})', { min: PROMPT_REPEAT_MIN_SECONDS })}
              </label>
              <input
                id="promptRepeat"
                type="number"
                min={PROMPT_REPEAT_MIN_SECONDS}
                max={60}
                value={draft.promptRepeatSeconds}
                onChange={(e) =>
                  patch(
                    'promptRepeatSeconds',
                    Math.max(PROMPT_REPEAT_MIN_SECONDS, Number(e.target.value) || PROMPT_REPEAT_MIN_SECONDS),
                  )
                }
              />
            </div>
            <div className="field">
              <span className="field-label">{t('Sound')}</span>
              <label className="check">
                <input
                  type="checkbox"
                  checked={draft.correctPingEnabled}
                  onChange={(e) => patch('correctPingEnabled', e.target.checked)}
                />
                {t('Subtle confirmation tone on correct answer')}
              </label>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label htmlFor="roundTransitionType">{t('Between-rounds animation')}</label>
              <select
                id="roundTransitionType"
                value={draft.roundTransitionType}
                onChange={(e) => patch('roundTransitionType', e.target.value as RoundTransitionType)}
              >
                <option value="FADE">{t('Fade')}</option>
                <option value="SLIDE">{t('Slide up')}</option>
                <option value="NONE">{t('None (instant)')}</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="roundTransitionMs">
                {t('Animation duration (ms, {min}–{max})', { min: ROUND_TRANSITION_MIN_MS, max: ROUND_TRANSITION_MAX_MS })}
              </label>
              <input
                id="roundTransitionMs"
                type="number"
                min={ROUND_TRANSITION_MIN_MS}
                max={ROUND_TRANSITION_MAX_MS}
                step={50}
                disabled={draft.roundTransitionType === 'NONE'}
                value={draft.roundTransitionMs}
                onChange={(e) =>
                  patch(
                    'roundTransitionMs',
                    Math.min(
                      ROUND_TRANSITION_MAX_MS,
                      Math.max(ROUND_TRANSITION_MIN_MS, Number(e.target.value) || 0),
                    ),
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* THE SAVE SITS ONLY WHERE IT SAVES SOMETHING. Learners, features, rewards
          and the PIN each write immediately through their own button; putting a
          second Save under them would offer to save what is already saved, and
          leaving it off the two tabs that DO share a draft would hide the button
          that commits them. */}
      {SAVES_DRAFT.includes(tab) && (
        <div className="card">
          <div className="form-actions">
            <button className="btn" onClick={save} disabled={busy}>
              {busy ? t('Saving…') : t('Save settings')}
            </button>
            {success && <span className="muted" role="status">{success}</span>}
            {error && <span className="error" role="alert" style={{ margin: 0 }}>{error}</span>}
          </div>
        </div>
      )}
    </section>
  );
}
